/**
 * Korean label dictionaries for machine-readable SaveTicker/Control Tower
 * values. Unknown keys intentionally fall back to the raw value so a new
 * provider field is still visible instead of silently hidden.
 */

export const CANARY_GATES: Record<string, { label: string; desc: string }> = {
  auth_failures_zero: {
    label: "인증 실패 없음",
    desc: "최근 창에서 401/403 등 인증 실패 raw 응답이 0건이어야 합니다.",
  },
  raw_collected: {
    label: "원본 수집",
    desc: "수집 창 안에 raw 스냅샷이 1건 이상 쌓였는지 확인합니다.",
  },
  features_collected: {
    label: "피처 수집",
    desc: "정규화된 feature observation이 1건 이상 생성됐는지 확인합니다.",
  },
  outbox_failed_zero: {
    label: "Outbox 실패 없음",
    desc: "뉴스 발행 outbox에 failed 상태가 없어야 합니다.",
  },
  jobs_failed_zero: {
    label: "채점 실패 없음",
    desc: "LLM 채점 job에 failed 상태가 없어야 합니다.",
  },
  core_lag_under_180s: {
    label: "Core 지연 180초 미만",
    desc: "md_ohlcv 최신 봉이 180초 이내로 유지되는지 확인합니다.",
  },
  scheduler_not_degraded: {
    label: "스케줄러 정상",
    desc: "수집 스케줄러가 degraded 상태가 아닌지 확인합니다.",
  },
};

export const METRIC_LABELS: Record<string, string> = {
  price: "현재가",
  regular_price: "정규장 가격",
  extended_price: "시간외 가격",
  regular_change_pct: "정규장 등락률",
  extended_change_pct: "시간외 등락률",
  display_change_pct: "표시 등락률",
  day_low: "당일 저가",
  day_high: "당일 고가",
  day_current: "당일 현재가",
  week52_low: "52주 최저",
  week52_high: "52주 최고",
  week52_current: "52주 현재가",
  turnover: "거래대금",
  market_cap: "시가총액",
  per: "PER",
  pbr: "PBR",
  eps: "EPS",
  revenue_ttm: "TTM 매출",
  revenue_latest: "최근 분기 매출",
  revenue_yoy_latest: "매출 YoY",
  dividend_yield: "배당수익률",
  roe: "ROE",
  short_interest_pct: "공매도 비중",
  days_to_cover: "커버 일수",
  analyst_count: "애널리스트 수",
  analyst_target_mean: "목표가 평균",
  analyst_target_low: "목표가 최저",
  analyst_target_high: "목표가 최고",
  analyst_upside_pct: "목표가 여력",
  volume_current_ratio: "거래량 비율",
  volume_avg: "평균 거래량",
  volume_days_counted: "집계 일수",
  volume_window_days: "집계 기간(일)",
  volume_bucket_minutes: "버킷(분)",
  volume_curve_points: "거래량 커브 포인트",
  options_today_volume: "옵션 거래량",
  put_call_ratio_volume: "Put/Call 거래량 비율",
  put_call_ratio_oi: "Put/Call 미결제 비율",
  max_pain: "맥스 페인",
  net_gamma_exposure: "순 감마 노출",
  gamma_per_1pct: "1% 감마",
  call_wall: "콜 월",
  put_wall: "풋 월",
  gamma_flip: "감마 플립",
  gamma_magnet: "감마 자석",
  options_reference_price: "옵션 기준가",
  options_days_to_expiry: "만기까지 일수",
  insider_net_value: "내부자 순매수",
  insider_buy_count: "내부자 매수 건수",
  insider_sell_count: "내부자 매도 건수",
  insider_window_days: "내부자 집계 기간(일)",
  next_poll_after_ms: "다음 폴링(ms)",
};

export const QUALITY_FLAG_LABELS: Record<string, string> = {
  source_units_unverified: "단위 미검증",
  as_of_unknown: "기준시각 미상",
  snapshot_prior_day: "전일 스냅샷",
  batch_prior_day: "배치 전일",
  batch_provisional: "잠정치",
  pbr_absent: "PBR 없음",
  quote_mismatch: "호가 불일치",
  headline_only: "헤드라인만",
  disclosure_unknown: "공시 미상",
  mapping_unverified: "매핑 미검증",
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  NEWS: "뉴스",
  ANALYST: "애널리스트",
  FILING: "공시",
  EARNINGS: "실적",
  DIVIDEND: "배당",
  SPLIT: "분할",
  OFFERING: "유상증자",
};

export const FILING_CATEGORY_LABELS: Record<string, string> = {
  insider_ownership: "내부자 지분",
  other: "기타",
  periodic: "정기 보고",
  current: "수시 보고",
  registration: "등록",
  proxy: "위임장",
};

export const FORM_LABELS: Record<string, string> = {
  "4": "Form 4 (내부자)",
  "3": "Form 3 (내부자)",
  "5": "Form 5 (내부자)",
  "8-K": "8-K (수시)",
  "10-Q": "10-Q (분기)",
  "10-K": "10-K (연간)",
  "13F": "13F (기관)",
  "13D": "13D (지분)",
  "13G": "13G (지분)",
  "S-1": "S-1 (등록)",
  "485APOS": "485APOS (ETF 정정)",
  "497": "497 (ETF 보충)",
};

export const SCORE_STATE_LABELS: Record<string, string> = {
  pending: "채점 대기",
  scored: "채점 완료",
};

export const JOB_STATE_LABELS: Record<string, string> = {
  pending: "대기",
  scoring: "채점 중",
  scored: "완료",
  failed: "실패",
  skipped: "건너뜀",
  published: "발행",
  publishing: "발행 중",
};

export const ORIGIN_LABELS: Record<string, string> = {
  live: "실시간",
  attachment: "첨부 import",
};

export const MAPPING_STATUS_LABELS: Record<string, string> = {
  verified: "검증됨",
  related_only: "관련 자산",
  provider_only: "제공자 전용",
  mapping_unverified: "매핑 미검증",
};

export const INSIDER_CODE_LABELS: Record<string, string> = {
  S: "매도",
  P: "매수",
  A: "부여",
  M: "옵션 행사",
  F: "세금 납부",
  G: "증여",
  D: "처분",
  C: "전환",
};

export const SESSION_LABELS: Record<string, string> = {
  PRE: "프리마켓",
  REGULAR: "정규장",
  POST: "애프터마켓",
  CLOSED: "마감",
};

export function labelOf(
  dictionary: Record<string, string>,
  value?: string | null,
  fallback = "—",
): string {
  if (!value) return fallback;
  return dictionary[value] ?? value;
}

export function gateLabel(name: string): { label: string; desc: string } {
  return CANARY_GATES[name] ?? { label: name, desc: name };
}
